import { delayRender, continueRender } from 'remotion';

const loadedFonts = {};
/**
 * @description Load a Google Font for use in Remotion.
 * @param meta
 * @param {string} style The font style we want to load. While each font has a different set of styles, common options are: normal, italic etc.
 * @param {Array} options
 * @returns An object with the following properties: fontFamily, unicodeRanges, fonts
 * @see [Documentation](https://www.remotion.dev/docs/google-fonts/load-font)
 */
const loadFonts = (meta, style, options) => {
    var _a, _b, _c, _d;
    const promises = [];
    const styles = style ? [style] : Object.keys(meta.fonts);
    for (const style of styles) {
        // Don't load fonts on server
        if (typeof FontFace === "undefined") {
            continue;
        }
        if (!meta.fonts[style]) {
            throw new Error(`The font ${meta.fontFamily} does not have a style ${style}`);
        }
        const weights = (_a = options === null || options === void 0 ? void 0 : options.weights) !== null && _a !== void 0 ? _a : Object.keys(meta.fonts[style]);
        for (const weight of weights) {
            if (!meta.fonts[style][weight]) {
                throw new Error(`The font ${meta.fontFamily} does not have a weight ${weight} in style ${style}`);
            }
            const subsets = (_b = options === null || options === void 0 ? void 0 : options.subsets) !== null && _b !== void 0 ? _b : Object.keys(meta.fonts[style][weight]);
            for (const subset of subsets) {
                //  Get font url from meta
                let font = (_d = (_c = meta.fonts[style]) === null || _c === void 0 ? void 0 : _c[weight]) === null || _d === void 0 ? void 0 : _d[subset];
                //  Check is font available in meta
                if (!font) {
                    throw new Error(`weight: ${weight} subset: ${subset} is not available for '${meta.fontFamily}'`);
                }
                //  Check is font already loaded
                let fontKey = `${meta.fontFamily}-${style}-${weight}-${subset}`;
                if (loadedFonts[fontKey]) {
                    continue;
                }
                //  Mark font as loaded
                loadedFonts[fontKey] = true;
                const handle = delayRender(`Fetching ${meta.fontFamily} font ${JSON.stringify({
                    style,
                    weight,
                    subset,
                })}`);
                //  Create font-face
                const fontFace = new FontFace(meta.fontFamily, `url(${font}) format('woff2')`, {
                    weight: weight,
                    style: style,
                    unicodeRange: meta.unicodeRanges[subset],
                });
                //  Load font-face
                const promise = fontFace
                    .load()
                    .then(() => {
                    var _a;
                    ((_a = options === null || options === void 0 ? void 0 : options.document) !== null && _a !== void 0 ? _a : document).fonts.add(fontFace);
                    continueRender(handle);
                })
                    .catch((err) => {
                    //  Mark font as not loaded
                    loadedFonts[fontKey] = false;
                    throw err;
                });
                promises.push(promise);
            }
        }
    }
    return {
        fontFamily: meta.fontFamily,
        fonts: meta.fonts,
        unicodeRanges: meta.unicodeRanges,
        waitUntilDone: () => Promise.all(promises).then(() => undefined),
    };
};

export { loadFonts as l };
